import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema } from './primitives.js';
const c = initContract();
/**
 * Project Billing (PART 13, PRJ-04) — the contract, verbatim from the
 * source: billing "converts APPROVED + BILLABLE + not-yet-Invoiced
 * timesheet entries (and milestones) into real Sales Invoices (same
 * INV-#### model as the Sales module — not a separate 'project invoice'
 * table)".
 *
 * The four-condition test (§5d, BR-PRJ-04-01): APPROVED and BILLABLE and
 * NOT INVOICED and NOT ADJUSTING. Miss any one and the system bills work
 * nobody approved, bills internal time, bills twice, or double-counts a
 * correction.
 *
 * ONE rate resolution (§5c, BR-PRJ-04-05 — the mode-era defect is dead):
 * a Time & Materials project prices each hour at the member's ROLE rate
 * from the project's rate card (fallback band: Senior Consultant); any
 * other billing type prices at the project's FLAT rate. No entry point may
 * resolve a rate any other way — the same week's work bills the same
 * amount whoever generates it.
 *
 * EXCLUSIONS ARE ALWAYS HONOURED (BR-PRJ-04-06/-07): each exclusion needs a
 * reason, lasts one run, never touches the timesheet — and the generate
 * step bills exactly the preview minus the exclusions, by construction:
 * the request carries the excluded ids; the server bills everything
 * currently unbilled EXCEPT them.
 *
 * Generating (BR-PRJ-04-02/-03/-16): creates an ordinary DRAFT sales
 * invoice (editable in Sales, posted there, corrected there by credit
 * note), stamps every included entry Invoiced-with-its-number in the same
 * transaction (never re-billed — structural), and records the run as an
 * append-only billing batch.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const BILLING_MESSAGES = {
    /** BR-PRJ-04-04 / ERR-PRJ-04-01. */
    nothingToBill: 'There is nothing to bill — no approved, billable, unbilled hours are included.',
    /** §15's empty state, with its green tick on screen. */
    allInvoiced: 'Everything billable has been invoiced.',
    /** BR-PRJ-04-09 / ERR-PRJ-04-04. */
    milestoneNotCompleted: 'Only a completed milestone can be invoiced.',
    milestoneAlreadyInvoiced: 'This milestone is already invoiced.',
    /** BR-PRJ-04-12 / ERR-PRJ-04-05 — word for word. */
    nothingNewAtPct: 'Nothing new to bill at this % complete',
    /** BR-PRJ-04-13 / ERR-PRJ-04-06. */
    progressWrongType: 'Progress billing is offered only on Fixed Price and Retainer projects.',
    /**
     * §5e: the exclusion panel's promise — and ERR-PRJ-04-02's answer. Excluding
     * a line does NOT change the timesheet, by design: the hours stay approved
     * and unbilled, so a later run can pick them up.
     */
    exclusionUntouched: 'The timesheet entry is untouched — it stays unbilled and can be invoiced later.',
    /** ERR-PRJ-04-08 — the prototype's Exclude line did nothing and said nothing. */
    exclusionNeedsReason: 'Excluding a line requires a reason.',
    /** §5g: the consumption card's formula, stated. */
    consumedFormula: 'Consumed = billed + unbilled approved value vs contract value.',
    /** §5g thresholds. */
    overEighty: 'Over 80% of contract value consumed.',
    overHundred: 'Contract value fully consumed — over budget.',
    /** A rate table must exist before hours can be priced (§2 prerequisites). */
    noRate: 'A rate table must be set up before hours can be priced.',
    /** Q-PRJ-04-6 leftover: cancelled projects still bill approved hours. */
    unbilledSubtitle: "Don't let these slip between billing periods.",
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const PreviewLineSchema = z
    .object({
    entryId: z.uuid(),
    workDate: IsoDateSchema,
    /** The ACCOUNT that logged the hour — a timesheet's subject (PRJ-03). */
    userId: z.uuid(),
    /**
     * That person's STAFF name where their account is joined to an employee
     * record, falling back to the account's own name where it is not.
     */
    employeeName: z.string().nullable(),
    /** The price band applied (T&M) or the fallback label (flat). */
    rateRole: z.string(),
    taskTitle: z.string(),
    hours: z.string(),
    /** The §5c resolution's answer for THIS line. */
    rate: MoneyStringSchema,
    /** hours × rate, rounded at the line (N-7 — the total adds up). */
    amount: MoneyStringSchema,
})
    .strict();
export const PreviewResponseSchema = z
    .object({
    projectId: z.uuid(),
    /** 'role' on Time & Materials, 'flat' otherwise (BR-PRJ-04-05). */
    rateResolution: z.enum(['role', 'flat']),
    currency: z.string(),
    lines: PreviewLineSchema.array(),
    totalHours: z.string(),
    totalAmount: MoneyStringSchema,
})
    .strict();
export const GenerateInvoiceRequestSchema = z
    .object({
    /**
     * BR-PRJ-04-06/-07: each exclusion carries its reason; the server bills
     * everything currently unbilled EXCEPT these — an excluded line can
     * never be swept in because inclusion is defined as its complement.
     */
    exclusions: z
        .object({ entryId: z.uuid(), reason: z.string().trim().min(1).max(500) })
        .strict()
        .array()
        .optional(),
})
    .strict();
export const GenerateResultSchema = z
    .object({
    invoiceId: z.uuid(),
    /** INV-#### — a REAL sales invoice, openable in Sales (BR-PRJ-04-15). */
    invoiceCode: z.string(),
    batchId: z.uuid(),
    entryCount: z.number().int().min(0),
    totalHours: z.string(),
    /** The invoice subtotal (hours value); tax is the invoice's own affair. */
    amount: MoneyStringSchema,
})
    .strict();
export const BudgetResponseSchema = z
    .object({
    projectId: z.uuid(),
    contractValue: MoneyStringSchema,
    /** Σ grand totals of every invoice this screen raised for the project. */
    invoicedTotal: MoneyStringSchema,
    /** The value of every approved, billable, unbilled, non-adjusting hour. */
    unbilledApprovedValue: MoneyStringSchema,
    /** BR-PRJ-04-10: invoiced + unbilled approved. */
    consumed: MoneyStringSchema,
    consumedPct: z.number().min(0),
    /** BR-PRJ-04-11: null under 80, 'amber' at 80+, 'red' at 100+. */
    alert: z.enum(['amber', 'red']).nullable(),
    /** BR-PRJ-04-12's memory: the % already billed by progress runs. */
    billedProgressPct: PctStringSchema,
})
    .strict();
export const ProgressBillRequestSchema = z
    .object({
    /** Bill TO this % complete; only the difference is billed. */
    toPct: PctStringSchema,
})
    .strict();
export const UnbilledRowSchema = z
    .object({
    projectId: z.uuid(),
    code: z.string(),
    name: z.string(),
    hours: z.string(),
    value: MoneyStringSchema,
    currency: z.string(),
})
    .strict();
export const BatchRowSchema = z
    .object({
    id: z.uuid(),
    kind: z.enum(['tm', 'milestone', 'progress']),
    invoiceId: z.uuid(),
    invoiceCode: z.string(),
    invoiceStatus: z.enum(['draft', 'posted', 'cancelled']),
    invoiceTotal: MoneyStringSchema,
    amount: MoneyStringSchema,
    hours: z.string(),
    entryCount: z.number().int().min(0),
    progressFrom: PctStringSchema.nullable(),
    progressTo: PctStringSchema.nullable(),
    createdAt: z.string(),
})
    .strict();
const projectParams = z.object({ projectId: z.uuid() }).strict();
export const projectBillingContract = c.router({
    preview: {
        method: 'GET',
        path: '/api/projects/:projectId/billing/preview',
        pathParams: projectParams,
        responses: { 200: PreviewResponseSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'The invoice preview: every entry passing the FOUR-CONDITION test (approved + billable + not invoiced + not adjusting, BR-PRJ-04-01), priced by the ONE resolution (BR-PRJ-04-05).',
    },
    generate: {
        method: 'POST',
        path: '/api/projects/:projectId/billing/generate',
        pathParams: projectParams,
        body: GenerateInvoiceRequestSchema,
        responses: { 201: GenerateResultSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Generate invoice: ONE transaction — a real DRAFT sales invoice (BR-PRJ-04-02/-16), every included entry stamped Invoiced-with-its-number (BR-PRJ-04-03, never re-billed), the run recorded as a batch. Exclusions ALWAYS honoured (BR-PRJ-04-07); refused when nothing is included (BR-PRJ-04-04).',
    },
    billMilestone: {
        method: 'POST',
        path: '/api/projects/milestones/:milestoneId/generate-invoice',
        pathParams: z.object({ milestoneId: z.uuid() }).strict(),
        body: z.object({}).strict().optional(),
        responses: { 201: GenerateResultSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-04-09: bill a COMPLETED milestone for its full amount — a real draft sales invoice; the milestone is marked invoiced and freezes.',
    },
    billProgress: {
        method: 'POST',
        path: '/api/projects/:projectId/billing/progress',
        pathParams: projectParams,
        body: ProgressBillRequestSchema,
        responses: { 201: GenerateResultSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-04-12/-13: Fixed Price / Retainer only — bills the DIFFERENCE between the chosen % and the % already billed, and records it so the next run subtracts it. "Nothing new to bill at this % complete" when there is none.',
    },
    budget: {
        method: 'GET',
        path: '/api/projects/:projectId/billing/budget',
        pathParams: projectParams,
        responses: { 200: BudgetResponseSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'BR-PRJ-04-10/-11: consumed = billed + unbilled approved value vs contract value; amber at 80%, red at 100%. The SAME figure PRJ-01 shows as Spent (Q-PRJ-01-1).',
    },
    unbilled: {
        method: 'GET',
        path: '/api/projects/billing/unbilled',
        responses: {
            200: z.object({ rows: UnbilledRowSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: `Unbilled time — ALL projects: every project's approved unbilled hours and value. "Don't let these slip between billing periods."`,
    },
    history: {
        method: 'GET',
        path: '/api/projects/:projectId/billing/history',
        pathParams: projectParams,
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('batches', BatchRowSchema),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Invoice history: every run and its REAL invoice, openable in the Sales module (BR-PRJ-04-15). PAGED (NFR-42) — a long-running project gains a batch every billing period and never loses one.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=project-billing.js.map